import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Home, Layers, Settings, Volume2, MapPin, ArrowRight, Check, ChevronRight } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Savoir-faire (sourced brief FLOW_OK "Résidentiel premium" + HomePage L270)
const features = [
  { icon: Home,     title: "Équipement robuste et haute performance", desc: "Solutions dimensionnées pour appartements, maisons de luxe et résidences premium." },
  { icon: Layers,   title: "Intégration ultra discrète",               desc: "Gainables invisibles qui s'intègrent sans compromis dans l'architecture intérieure." },
  { icon: Settings, title: "Régulation intelligente",                  desc: "Pilotage fin et précis des températures dans chaque pièce de l'habitat." },
  { icon: Volume2,  title: "Fonctionnement silencieux",                desc: "Confort quotidien préservé, y compris dans les espaces nuit et de travail." }
];

// Référence (Studio Calogero — usage résidentiel sourcé)
const reference = {
  title: "Studio Calogero",
  location: "Boulogne-Billancourt",
  category: "Résidentiel & Studio",
  description: "Projet combinant espace de création musicale professionnelle et habitat, nécessitant une approche acoustique de très haut niveau. Climatisation et ventilation intégrées dans une isolation de type boîte dans la boîte.",
  specs: ["Isolation acoustique boîte dans la boîte", "Ventilation silencieuse NC15", "Double usage studio/résidentiel"]
};

const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function ResidentielPage() {
  return (
    <>
      <Helmet>
        <title>Climatisation résidentiel luxe Paris | FLOW</title>
        <meta name="description" content="FLOW installe des systèmes gainables invisibles à intégration ultra discrète pour le résidentiel premium : appartements et maisons de luxe. Référence : Studio Calogero (Boulogne-Billancourt, usage résidentiel)." />
        <link rel="canonical" href="https://flow-air.com/secteurs/residentiel" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="residentiel-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Secteur — Résidentiel</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Climatisation pour <span className="text-[#00AFFF]">résidentiel premium</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW installe des systèmes de climatisation robustes, haute performance
              et à intégration ultra discrète pour les appartements et maisons de luxe.
              Gainables invisibles et régulation intelligente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Savoir-faire */}
      <section className="py-16 md:py-24 bg-white" data-testid="residentiel-features">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Savoir-faire</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Caractéristiques résidentiel
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`residentiel-feature-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{feature.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Référence */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]" data-testid="residentiel-reference">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Réalisation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Notre référence résidentiel
            </h2>
          </motion.div>

          <motion.article
            className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-[#E2E8F0] max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="residentiel-reference-card"
          >
            <div className="flex items-center gap-2 text-[#475569] text-sm mb-3">
              <MapPin size={14} className="text-[#00AFFF]" />
              <span>{reference.location}</span>
              <span className="mx-2">·</span>
              <span className="px-2 py-0.5 bg-[#00AFFF]/10 text-[#00AFFF] rounded-full text-xs font-medium">{reference.category}</span>
            </div>
            <h3 className="text-2xl font-bold text-[#0E1A2B] mb-4">{reference.title}</h3>
            <p className="text-[#475569] leading-relaxed mb-6">{reference.description}</p>
            <ul className="space-y-2">
              {reference.specs.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-[#0E1A2B]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00AFFF] mt-2 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </motion.article>

          <div className="text-center mt-10">
            <Link
              to="/realisations"
              className="text-[#00AFFF] text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="residentiel-see-all"
            >
              Voir toutes nos réalisations <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Badges */}
      <section className="py-12 bg-white">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F5F7FA] rounded-lg">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en climatisation et ventilation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="residentiel-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
